import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatGridListModule } from '@angular/material/grid-list'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { ProductSearchComponent } from '../product-search/product-search.component'
import { MatMenuModule } from '@angular/material/menu'
import { MatButtonModule } from '@angular/material/button'
import { MatBadgeModule } from '@angular/material/badge'
import { SideMenuComponent } from '../side-menu/side-menu.component'
import { NavbarService } from '../../services/navbar.service'
import { AuthService } from '../../services/auth.service'
import { Router, RouterModule } from '@angular/router'
import { SpinnerService } from '../../services/spinner.service'
import { firstValueFrom } from 'rxjs'
import { ApiError } from '../../types/apiError'
import { ErrorService } from '../../services/error.service'
import { DialogService } from '../../services/dialog.service'
import { Dialog } from '@angular/cdk/dialog'
import { TranslationService } from '../../services/translation.service'
import { MarketplaceService } from '../../services/marketplace.service'
import { LocationService } from '../../services/location.service'
import {
  CanadianProvinceName,
  RecyclabilityCountry,
  RecyclabilityLocation,
} from '../../types/recyclability'
import { LocationFromApi } from '../../types/locations'
import { RECYCLABILITY_COUNTRIES } from '../../constants/recyclability'
import { CANADIAN_PROVINCES } from '../../constants/provinces'

@Component({
  selector: 'app-header-navbar',
  imports: [
    MatToolbarModule,
    MatGridListModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    ProductSearchComponent,
    MatMenuModule,
    MatButtonModule,
    MatBadgeModule,
    SideMenuComponent,
    RouterModule,
  ],
  templateUrl: './header-navbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header-navbar.component.scss',
})
export class HeaderNavbarComponent implements OnInit {
  @ViewChild(SideMenuComponent) sideMenuComponent!: SideMenuComponent
  isSideMenuVisible = false

  navbarService = inject(NavbarService)
  router = inject(Router)
  authService = inject(AuthService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  dialogService = inject(DialogService)
  dialog = inject(Dialog)
  translationService = inject(TranslationService)
  marketplace = inject(MarketplaceService)
  locationService = inject(LocationService)
  userLocation: RecyclabilityLocation | null = null

  isMarketplacePage(): boolean {
    return /^\/marketplace(?:\/|[?#]|$)/.test(this.router.url)
  }

  ngOnInit(): void {
    this.resolveUserLocation()
  }

  /**
   * Resolve the user location checking the address in local storage and if the location in local storage is not
   * available or expired get a new location from the api
   */
  resolveUserLocation() {
    this.userLocation = this.locationService.getLocation()

    if (this.userLocation) {
      return
    }

    this.spinnerService.startSpinner()
    firstValueFrom<LocationFromApi>(this.locationService.getLocationApi())
      .then((location) => {
        if (!location.country || !location.province) {
          location.country =
            RECYCLABILITY_COUNTRIES.CANADA as RecyclabilityCountry
          location.province = CANADIAN_PROVINCES[0].name
        }

        if (
          !Object.values(RECYCLABILITY_COUNTRIES).some(
            (country) => country === location.country,
          )
        ) {
          location.country =
            RECYCLABILITY_COUNTRIES.CANADA as RecyclabilityCountry
        }

        if (
          !CANADIAN_PROVINCES.some(
            (province) => province.name === location.province,
          )
        ) {
          location.province = CANADIAN_PROVINCES[0].name
        }

        this.locationService.storeLocationInLS({
          country: location.country as RecyclabilityCountry,
          province: location.province as CanadianProvinceName,
          expiry: new Date().getTime() + 7 * 24 * 60 * 60 * 1000,
        })
      })
      .catch((err) => {
        const apiErr = err as ApiError
        this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
      })
      .finally(() => this.spinnerService.stopSpinner())
  }

  /**
   * Toggles Visibility of Side Menu for mobile devices
   */
  toggleSideMenuVisibility() {
    this.sideMenuComponent.updateCurrentRoute()
    this.isSideMenuVisible = !this.isSideMenuVisible
  }

  /**
   * Reroutes user to login page
   */
  login() {
    this.router.navigate(['/auth/login'])
  }

  /**
   * Logs user out and reroutes them to the home page
   */
  async logout() {
    try {
      this.spinnerService.startSpinner()
      await firstValueFrom(this.authService.logout())
      this.spinnerService.stopSpinner()
      this.router.navigate(['/auth/login'])
      this.authService.currentUserRole.set('Guest')
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Routes user to settings screen
   */
  goToSettings() {
    this.router.navigate(['/settings'])
  }
}
